import React, { memo, useEffect, useMemo, useRef } from 'react';
import { cn } from '@rocco/utils/cn';
import { Skeleton } from '@rocco/components/skeleton';
import { pluralize } from '~/utils';
import { getUserOccupation, useUserLoggedIn } from '~/modules/user';
import type { ContactSuggestion, Conversation } from '../../types';
import {
    getChatParticipantsExcludingSelf,
    getChatTitle,
    getParticipantName,
} from '../../utils';
import { ParticipantAvatar } from '../avatar/ParticipantAvatar';
import { ConversationAvatar } from '../avatar/ConversationAvatar';

/**
 * A list of new message contact suggestions in the new message chat box.
 * In forward mode, also shows a "Chats" section with existing conversations.
 * @see {@link ChatBox}
 */
export function ChatBoxSuggestionsList({
    className,
    suggestions,
    conversations,
    isLoading = false,
    hasMore = false,
    onSuggestionClick,
    onConversationClick,
    onLoadMore,
}: {
    className?: string;
    suggestions: ContactSuggestion[];
    conversations?: Conversation[];
    isLoading?: boolean;
    hasMore?: boolean;
    onSuggestionClick: (suggestion: ContactSuggestion) => void;
    onConversationClick: (conversation: Conversation) => void;
    onLoadMore?: () => void;
}) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const loadMoreSentinelRef = useRef<HTMLDivElement>(null);

    // Trigger load more suggestions when the user scrolls to the bottom.
    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        const scrollContainer = scrollContainerRef.current;
        if (!sentinel || !scrollContainer || !hasMore || isLoading) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (
                    entry?.isIntersecting &&
                    onLoadMore &&
                    hasMore &&
                    !isLoading
                ) {
                    onLoadMore();
                }
            },
            { root: scrollContainer },
        );
        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [hasMore, isLoading, onLoadMore]);

    return (
        <section
            ref={scrollContainerRef}
            className={cn(
                'flex flex-col gap-[8px] overflow-y-auto overscroll-contain',
                className,
            )}
        >
            {/* Chats section (forward mode) */}
            {conversations && conversations.length > 0 && (
                <>
                    <h3 className="text-[12px] leading-tight text-gray-600">
                        Chats
                    </h3>
                    <ul className="flex flex-col divide-y divide-black/20">
                        {conversations.map((conversation) => (
                            <ConversationItem
                                key={conversation.ID}
                                conversation={conversation}
                                onSelect={() =>
                                    onConversationClick(conversation)
                                }
                            />
                        ))}
                    </ul>
                </>
            )}
            {/* Contact suggestions section */}
            <h3 className="text-[12px] leading-tight text-gray-600">
                Suggestions
            </h3>
            <ul
                className="relative flex flex-col divide-y divide-black/20"
                aria-label="Contact suggestions"
                aria-live="polite"
                aria-busy={isLoading}
            >
                {suggestions.length ? (
                    <>
                        {suggestions.map((suggestion) => (
                            <SuggestionItem
                                key={suggestion.ID}
                                avatar={
                                    <ParticipantAvatar
                                        className="size-[36px]"
                                        fallbackClassName="text-[14px]!"
                                        participant={suggestion}
                                        showUserStatus
                                    />
                                }
                                title={suggestion.Name}
                                subtitle={getUserOccupation(suggestion)}
                                aria-label="Add to recipients"
                                onSelect={() => onSuggestionClick(suggestion)}
                            />
                        ))}
                        {hasMore && (
                            <div
                                ref={loadMoreSentinelRef}
                                className="invisible absolute inset-x-0 bottom-[500px] h-px"
                                aria-hidden="true"
                            ></div>
                        )}
                    </>
                ) : isLoading ? (
                    <SuggestionsListSkeleton />
                ) : (
                    <li className="p-[8px] text-center text-[16px] leading-tight text-gray-600">
                        No suggestions found
                    </li>
                )}
            </ul>
        </section>
    );
}

function SuggestionItem({
    avatar,
    title,
    subtitle,
    onSelect,
    ...props
}: React.ComponentProps<'li'> & {
    avatar: React.ReactNode;
    title: string;
    subtitle: string;
    onSelect: () => void;
}) {
    return (
        <li
            className="flex items-center gap-[8px] p-[8px] hover:bg-black/10 focus-visible:bg-black/10"
            role="button"
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect();
                }
            }}
            {...props}
        >
            {avatar}
            <div className="min-w-0 flex-1 space-y-[2px]">
                <div className="truncate text-[14px] font-medium leading-tight">
                    {title}
                </div>
                <div className="truncate text-[12px] leading-tight opacity-70">
                    {subtitle}
                </div>
            </div>
        </li>
    );
}

function ConversationItem({
    conversation,
    onSelect,
}: {
    conversation: Conversation;
    onSelect: () => void;
}) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const title = useMemo(() => {
        return getChatTitle(conversation, isLoggedIn ? user.ID : 0);
    }, [conversation, isLoggedIn, user.ID]);
    const subtitle = useMemo(() => {
        if (conversation.__typename === 'EnquiryConversation') {
            return 'Professional';
        }
        if (conversation.IsGroupChat) {
            const count = conversation.Participants.length;
            return `Group chat · ${pluralize(count, 'participant')}`;
        }
        // DirectConversation (non-group)
        if (conversation.Name) {
            const otherParticipants = getChatParticipantsExcludingSelf(
                conversation,
                isLoggedIn ? user.ID : 0,
                null,
            );
            // Custom title is set, show the other participant's name.
            const participant = otherParticipants[0];
            if (participant) {
                return getParticipantName(participant);
            }
        }
        return 'User chat';
    }, [conversation, isLoggedIn, user.ID]);

    return (
        <SuggestionItem
            avatar={
                <ConversationAvatar
                    className="size-[36px]"
                    avatarClassName="size-[36px]"
                    fallbackClassName="text-[14px]!"
                    conversation={conversation}
                    professionalID={null}
                    showActiveStatus
                />
            }
            title={title}
            subtitle={subtitle}
            aria-label="Select this chat"
            onSelect={onSelect}
        />
    );
}

const SuggestionsListSkeleton = memo(function SuggestionsListSkeleton() {
    return (
        <>
            {Array.from({ length: 8 }).map((_, index) => (
                <li key={index} className="flex items-center gap-[8px] p-[8px]">
                    <Skeleton className="size-[36px] rounded-full bg-gray-500" />
                    <div className="flex-1 space-y-[4px]">
                        <Skeleton className="h-[14px] w-1/2 bg-gray-500" />
                        <Skeleton className="h-[10px] w-1/6 bg-gray-500" />
                    </div>
                </li>
            ))}
        </>
    );
});
